import { BLOCKS, type BlockId, type RuleDef, type RuleId } from './rules.js';
import { DEFAULT_SCORING, blockOfAlert, isTopSegment, type ScoringConfig } from './scoring.js';
import type {
  Agreement,
  Alert,
  Light,
  PrevAlert,
  Segment,
  Tandem,
  TandemSheet,
  UpcomingMeeting,
} from './types.js';

export const MAX_TABLE = DEFAULT_SCORING.table.max;
export const MAX_SHEETS_PER_TANDEM = 3;

export type SegmentFilter = 'AB' | 'ALL';
export interface BoardFilter {
  segment: SegmentFilter;
  affId: string | null;
}
export const DEFAULT_FILTER: BoardFilter = { segment: 'AB', affId: null };

const segmentOk = (f: BoardFilter, s: Segment | null) => f.segment === 'ALL' || isTopSegment(s);

export function filterAlerts<T extends Pick<Alert, 'segment' | 'affId'>>(alerts: T[], f: BoardFilter): T[] {
  return alerts.filter((a) => segmentOk(f, a.segment) && (!f.affId || a.affId === f.affId));
}

export type BoardAlert = Alert & {
  broughtByTandem: boolean;
  /** Otras reglas del mismo caso (ticket/junta) cuando la mesa agrupa por caso */
  related?: Alert[];
};

/** Marca `trae_tandem`: la alerta está en alguna hoja de tándem de la semana. */
export function withTandemFlag(alerts: Alert[], sheets: TandemSheet[]): BoardAlert[] {
  const keys = new Set(sheets.map((s) => s.caseKey));
  return alerts.map((a) => ({ ...a, broughtByTandem: keys.has(a.key) }));
}

const byScore = (x: Alert, y: Alert) => y.score - x.score || x.key.localeCompare(y.key);

const LIGHT_RANK = { rojo: 0, ambar: 1, verde: 2 } as const;
/** Nivel del caso: 0 = segmento A/B, 1 = el resto */
const tierOf = (a: Pick<Alert, 'segment'>) => (isTopSegment(a.segment) ? 0 : 1);

/** Comparador de la mesa según `ordering`. */
export function tableComparator(cfg: ScoringConfig = DEFAULT_SCORING) {
  if (cfg.table.ordering === 'score') return byScore;
  return (x: Alert, y: Alert) =>
    tierOf(x) - tierOf(y) || LIGHT_RANK[x.light] - LIGHT_RANK[y.light] || byScore(x, y);
}

const caseKey = (a: Pick<Alert, 'officeId' | 'objectType' | 'objectId'>) => `${a.objectType}:${a.objectId}`;

/**
 * Un sitio por caso: agrupa las alertas del mismo ticket/junta. La principal es la primera según el
 * comparador de la mesa; el resto va en `related`. El caso cuenta como traído si cualquiera de sus reglas lo está.
 */
export function groupCases<T extends Alert & { broughtByTandem?: boolean }>(
  alerts: T[],
  cmp: (x: Alert, y: Alert) => number = byScore,
): (T & { related?: Alert[] })[] {
  const groups = new Map<string, T[]>();
  for (const a of alerts) groups.set(caseKey(a), [...(groups.get(caseKey(a)) ?? []), a]);
  return [...groups.values()].map((g) => {
    const [main, ...rest] = [...g].sort(cmp);
    if (!rest.length) return main!;
    return {
      ...main!,
      related: rest,
      ...(main!.broughtByTandem !== undefined && { broughtByTandem: g.some((a) => a.broughtByTandem) }),
    };
  });
}

/** Plazas por bloque: proporcional a sus minutos (quota) o un mínimo fijo (minimum). */
function blockQuotas(blocks: BlockId[], max: number, cfg: ScoringConfig) {
  const quota = new Map<BlockId, number>();
  if (cfg.table.allocation === 'minimum') {
    for (const b of blocks) quota.set(b, Math.min(cfg.table.minPerBlock, max));
    return quota;
  }
  // Reparto proporcional a los minutos de cada bloque con alertas (método del mayor resto)
  const total = blocks.reduce((s, b) => s + (cfg.blockMinutes[b] || 0), 0) || 1;
  const raw = blocks.map((b) => ({ b, q: (max * (cfg.blockMinutes[b] || 0)) / total }));
  for (const { b, q } of raw) quota.set(b, Math.floor(q));
  let left = max - [...quota.values()].reduce((s, n) => s + n, 0);
  for (const { b } of [...raw].sort((x, y) => (y.q % 1) - (x.q % 1))) {
    if (left <= 0) break;
    quota.set(b, quota.get(b)! + 1);
    left--;
  }
  return quota;
}

/**
 * La mesa: `max` casos tras el filtro. Pasos:
 * 1. (opcional) un sitio por caso;
 * 2. orden por niveles (A/B → resto; rojo → ámbar; puntuación) o solo puntuación;
 * 3. reparto: global, cupo por bloque o mínimo por bloque. Con orden por niveles el reparto se hace
 *    nivel a nivel: primero se reparten las plazas entre los casos A/B (cupo y luego sobrantes) y solo
 *    las plazas libres pasan a C/D/E. Así la mesa de «Todos» siempre contiene la de «A + B».
 * El resto es la capa de detalle, en el mismo orden.
 */
export function buildTable<T extends BoardAlert | Alert>(
  alerts: T[],
  f: BoardFilter,
  opts: { scoring?: ScoringConfig; max?: number } = {},
) {
  const cfg = opts.scoring ?? DEFAULT_SCORING;
  const max = opts.max ?? cfg.table.max;
  const cmp = tableComparator(cfg);
  const filtered = filterAlerts(alerts, f);
  const visible = (cfg.table.groupByCase ? groupCases(filtered, cmp) : filtered).sort(cmp) as (T & {
    related?: Alert[];
  })[];

  let inside: typeof visible;
  if (cfg.table.allocation === 'global') inside = visible.slice(0, max);
  else {
    const blocks = [...new Set(visible.map((a) => blockOfAlert(a)))];
    const quota = blockQuotas(blocks, max, cfg);
    const taken = new Map<BlockId, number>(blocks.map((b) => [b, 0]));
    const picked = new Set<(typeof visible)[number]>();
    const tiers = cfg.table.ordering === 'tiered' ? [0, 1] : [null];
    for (const tier of tiers) {
      const pool = visible.filter((a) => tier === null || tierOf(a) === tier);
      // a) cupo de cada bloque
      for (const a of pool) {
        if (picked.size >= max) break;
        const b = blockOfAlert(a);
        if (taken.get(b)! < quota.get(b)!) {
          picked.add(a);
          taken.set(b, taken.get(b)! + 1);
        }
      }
      // b) plazas que los bloques de este nivel no han llenado, por orden
      for (const a of pool) {
        if (picked.size >= max) break;
        if (!picked.has(a)) {
          picked.add(a);
          taken.set(blockOfAlert(a), taken.get(blockOfAlert(a))! + 1);
        }
      }
    }
    inside = visible.filter((a) => picked.has(a));
  }
  const set = new Set(inside);
  return { inside, outside: visible.filter((a) => !set.has(a)) };
}

export function groupByBlock<T extends Alert>(alerts: T[], _rules?: Record<RuleId, RuleDef>) {
  return BLOCKS.map((b) => ({ block: b, alerts: alerts.filter((a) => blockOfAlert(a) === b.id) }));
}

export interface TandemSummary extends Tandem {
  /** Casos fuera de umbral (no alertas por regla) */
  alerts: number;
  inTable?: number;
  brought: BoardAlert[];
  silent: boolean;
}

/**
 * Casos distintos (ticket/junta) de una lista de alertas, con su peor semáforo. Una incidencia que está
 * fuera de umbral por S1 y S2 es un caso, no dos. Las alertas sin objectId (datos antiguos) cuentan una a una.
 */
export function distinctCases<
  T extends Pick<Alert, 'light' | 'ruleId'> & Partial<Pick<Alert, 'objectType' | 'objectId'>>,
>(list: T[]): { light: Alert['light'] }[] {
  const m = new Map<string, Alert['light']>();
  list.forEach((a, i) => {
    const k = a.objectId ? `${a.objectType}:${a.objectId}` : `#${i}`;
    const prev = m.get(k);
    if (!prev || LIGHT_RANK[a.light] < LIGHT_RANK[prev]) m.set(k, a.light);
  });
  return [...m.values()].map((light) => ({ light }));
}

/**
 * Lo que trae cada tándem. `alerts` = **casos** fuera de umbral en su cartera (con el filtro de segmento);
 * `inTable` = cuántos de esos casos están en la mesa (si se pasa `table`).
 */
export function summarizeTandems(
  tandems: Tandem[],
  alerts: BoardAlert[],
  f: BoardFilter,
  table?: { inside: Alert[] },
): TandemSummary[] {
  const segFiltered = filterAlerts(alerts, { segment: f.segment, affId: null });
  const inTableCases = new Set((table?.inside ?? []).map((a) => caseKey(a)));
  return tandems.map((t) => {
    const mine = groupCases(segFiltered.filter((a) => a.affId === t.affId));
    const brought = mine.filter((a) => a.broughtByTandem).slice(0, MAX_SHEETS_PER_TANDEM);
    return {
      ...t,
      alerts: mine.length,
      inTable: table ? mine.filter((a) => inTableCases.has(caseKey(a))).length : undefined,
      brought,
      silent: brought.length === 0 && mine.length > 0,
    };
  });
}

export interface Sample {
  alert: BoardAlert;
  reason: 'silent' | 'top';
  questions: string[];
}

const QUESTIONS = [
  '¿Qué decisión necesitas de mí?',
  '¿Dónde está el bloqueo real?',
  '¿Lo sabe el presidente?',
  '¿Esto es un caso o es un patrón?',
];

/** Muestreo del DO: 2 alertas no traídas; primero las del tándem silencioso, luego por puntuación. */
export function pickSamples(tandems: Tandem[], alerts: BoardAlert[], f: BoardFilter, n = 2): Sample[] {
  const silent = new Set(
    summarizeTandems(tandems, alerts, f)
      .filter((t) => t.silent)
      .map((t) => t.affId),
  );
  const isSilent = (a: BoardAlert) => (a.affId && silent.has(a.affId) ? 1 : 0);
  // Un sitio por caso también en el muestreo: no se muestrea dos veces el mismo ticket
  return groupCases(filterAlerts(alerts, f), byScore)
    .filter((a) => !a.broughtByTandem)
    .sort((x, y) => isSilent(y) - isSilent(x) || byScore(x, y))
    .slice(0, n)
    .map((alert, i) => ({
      alert,
      reason: isSilent(alert) ? 'silent' : 'top',
      questions: QUESTIONS.slice(i * 2, i * 2 + 2),
    }));
}

export type KpiId = 'K1' | 'K2' | 'K3' | 'K4' | 'K5' | 'K6';

export interface Kpi {
  id: KpiId;
  label: string;
  value: number;
  /** null cuando no hay foto anterior o se filtra por AFF */
  delta: number | null;
  sub: string;
  light: Light;
  source: RuleDef['source'];
  sourceLabel: string;
  /** false si ninguna regla del KPI está habilitada */
  available: boolean;
}

const KPI_RULES: { id: Exclude<KpiId, 'K1'>; label: string; rules: RuleId[]; sourceLabel: string }[] = [
  { id: 'K2', label: 'Actas fuera de plazo', rules: ['J1'], sourceLabel: 'Eleva · juntas' },
  {
    id: 'K3',
    label: 'Incidencias fuera de umbral',
    rules: ['S1', 'S2', 'S3'],
    sourceLabel: 'Eleva · tickets',
  },
  {
    id: 'K4',
    label: 'Siniestros, presupuestos y obras',
    rules: ['S4', 'S5', 'S6', 'S7'],
    sourceLabel: 'Siniestros · Obria · Eleva',
  },
  { id: 'K5', label: 'Reclamaciones fuera de SLA', rules: ['C1', 'C2'], sourceLabel: 'Atención al cliente' },
  {
    id: 'K6',
    label: 'Tesorería, deuda y altas',
    rules: ['F1', 'F2', 'F3'],
    sourceLabel: 'Kontea · contabilidad',
  },
];

const worst = (list: Pick<Alert, 'light'>[]): Light =>
  list.some((a) => a.light === 'rojo') ? 'rojo' : list.length ? 'ambar' : 'verde';

/** Pulso de la oficina: 6 tarjetas con delta frente a la foto anterior. */
export function computeKpis(input: {
  alerts: Alert[];
  previousAlerts: PrevAlert[] | null;
  meetings: UpcomingMeeting[];
  previousMeetings: Pick<UpcomingMeeting, 'segment' | 'affId' | 'sensitive'>[] | null;
  rules: Record<RuleId, RuleDef>;
  filter: BoardFilter;
}): Kpi[] {
  const { rules, filter } = input;
  const cur = filterAlerts(input.alerts, filter);
  const prev = input.previousAlerts ? filterAlerts(input.previousAlerts, filter) : null;
  const deltaOk = !filter.affId;

  const meetings = filterAlerts(input.meetings, filter);
  const sensitive = meetings.filter((m) => m.sensitive).length;
  const prevMeetings = input.previousMeetings ? filterAlerts(input.previousMeetings, filter) : null;
  const k1: Kpi = {
    id: 'K1',
    label: 'Juntas próximas 4 semanas',
    value: meetings.length,
    delta: deltaOk && prevMeetings ? meetings.length - prevMeetings.length : null,
    sub: `${sensitive} sensibles · ${meetings.filter((m) => m.days <= 14).length} en 2 semanas`,
    light: sensitive >= 3 ? 'rojo' : sensitive ? 'ambar' : 'verde',
    source: 'ok',
    sourceLabel: 'Eleva · juntas',
    available: true,
  };

  return [
    k1,
    ...KPI_RULES.map((k): Kpi => {
      // Se cuentan casos: un ticket fuera de umbral por S1 y S2 es una incidencia, no dos
      const list = distinctCases(cur.filter((a) => k.rules.includes(a.ruleId)));
      const prevN = prev ? distinctCases(prev.filter((a) => k.rules.includes(a.ruleId))).length : null;
      const enabled = k.rules.filter((id) => rules[id]?.enabled);
      const available = enabled.length > 0;
      return {
        id: k.id,
        label: k.label,
        value: list.length,
        delta: deltaOk && prevN != null ? list.length - prevN : null,
        sub: `${list.filter((a) => a.light === 'rojo').length} en rojo`,
        light: worst(list),
        source: available ? (enabled.length === k.rules.length ? 'ok' : 'warn') : 'bad',
        sourceLabel: k.sourceLabel,
        available,
      };
    }),
  ];
}

export function agreementsSemaphore(list: Agreement[]) {
  const c = { verde: 0, ambar: 0, rojo: 0, pendiente: 0 };
  for (const a of list) c[a.status ?? 'pendiente']++;
  const rated = c.verde + c.ambar + c.rojo;
  return { ...c, pctOnTime: rated ? Math.round((c.verde / rated) * 100) : null };
}

/** Tiempo por bloque en la mesa. */
export function blockLoad(blockId: BlockId, count: number, cfg: ScoringConfig = DEFAULT_SCORING) {
  const minutes = cfg.blockMinutes[blockId] ?? BLOCKS.find((x) => x.id === blockId)!.minutes;
  return { minutes, perCase: count ? Math.round(minutes / count) : null };
}

/** Catálogo de tarjetas del pulso (para el panel de admin). */
export const KPI_DEFS: { id: KpiId; label: string; rules: RuleId[] }[] = [
  { id: 'K1', label: 'Juntas próximas 4 semanas', rules: [] },
  ...KPI_RULES.map((k) => ({ id: k.id, label: k.label, rules: k.rules })),
];

/** Qué se muestra en la pantalla de la reunión (global, se edita en el admin). */
export interface DisplayConfig {
  hiddenKpis: KpiId[];
}
export const DEFAULT_DISPLAY: DisplayConfig = { hiddenKpis: [] };
export const normalizeDisplay = (c: Partial<DisplayConfig> | null | undefined): DisplayConfig => ({
  hiddenKpis: (c?.hiddenKpis ?? []).filter((id) => KPI_DEFS.some((k) => k.id === id)),
});
